import { PageTitle } from '../components/atoms/PageTitle'
import { useNavigate, useParams } from 'react-router-dom'
import { Device, ID } from '../types/models'
import { Trans, useTranslation } from 'react-i18next'
import { TopTitle } from '../components/atoms/TopTitle'
import { PageText } from '../components/atoms/PageText'
import { useApp } from '../hooks/useApp'
import { DeviceDiagram } from '../components/DeviceDiagram'
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Link } from '@mui/material'
import { PortView } from '../components/PortView'
import { ArrowForwardIos } from '@mui/icons-material'
import { isDebug } from '../utils/misc'
import { callAxios } from '../hooks/useAxios'
import { PortNumber } from '../types/ports'
import React, { useEffect, useState } from 'react'
import { useDevices } from '../hooks/useDevices'
import { ConfigDiffList } from '../components/ConfigDiffList'

export function DeviceConfigPage(): React.ReactElement {

  const { t } = useTranslation()
  const app = useApp()
  const navigate = useNavigate()
  const { portId, id } = useParams()
  const [scrolled, setScrolled] = useState(false)

  const { control, onUpdate, setDevices } = useDevices()
  const deviceControl = control.controls({ deviceId: parseInt(id || '0') as ID })[0]
  const device = deviceControl ? deviceControl.device : undefined

  const onAlterPort = (portNumber: PortNumber, params: { name?: string, rule_id?: ID }) => {
    device && onUpdate({ hw_id: device.hw_id, port: { ...params, port_number: portNumber } })
  }

  // For root view, build data dynamically.
  useEffect(() => {
    if (app.user?.role === 'ROOT') {
      callAxios<Device>({
        method: 'GET',
        url: `/devices/${id}`,
      }).then(res => {
        if (res.success) {
          const device = res.data
          setDevices([device])
        } else {
          app.addError(res)
        }
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const selectedGroup = portId && control ? deviceControl.findGroupOfPort(parseInt(portId) as ID) : null
  const [groupOpened, setGroupOpened] = useState<Set<string>>(selectedGroup ? new Set([selectedGroup]) : new Set())
  const [portOpened, setPortOpened] = useState<Set<number>>(portId ? new Set([parseInt(portId)]) : new Set())

  // Scroll to the port.
  useEffect(() => {
    if (!scrolled) {
      const paramPort = deviceControl && deviceControl.findPortBy(p => p.port.id === parseInt(portId || '0') as ID)
      if (paramPort) {
        paramPort.gotoPort(parseInt(portId || '0') as ID)
        setScrolled(true)
      }
    }

    setGroupOpened(selectedGroup ? new Set([selectedGroup]) : new Set())
    setPortOpened(portId ? new Set([parseInt(portId)]) : new Set())
  }, [deviceControl, portId, scrolled, selectedGroup])

  if (!device) {
    return <></>
  }

  const onDone = (): void => {
    navigate(`/save/${device.id}/${portId || ''}`)
  }
  const onToggleGroup = (group: string, state: boolean) => {
    if (state) {
      setGroupOpened(new Set([...groupOpened, group]))
    } else {
      setGroupOpened(new Set([...groupOpened].filter(g => g !== group)))
    }
  }
  const onTogglePort = (port: number, state: boolean) => {
    if (state) {
      setPortOpened(new Set([...portOpened, port]))
    } else {
      setPortOpened(new Set([...portOpened].filter(g => g !== port)))
    }
  }

  return (
    <>
      <TopTitle title={t('Device Configuration')}></TopTitle>
      {
        isDebug('port-numbers') && <PageText sx={{ mb: '1.2rem' }}>[#{device.id} {device.hw_id}]</PageText>
      }

      <Alert severity="info" sx={{ mb: '1.5rem' }} data-cy="advanced-users-note">
        {t('This page is for advanced users who know what they are doing.')}{' '}
        {t('If you need help, contact')}{' '}
        <Link href="mailto:tuki@enion.fi">tuki@enion.fi</Link>.
      </Alert>

      <DeviceDiagram device={device} sx={{ mb: '2rem' }}/>
      {
        device && device.id && control.getDeviceControl(device.id).hasProblems() &&
        <ConfigDiffList control={control} id={device.id} diff={control.getDeviceControl(device.id).hardwareDiff()}/>
      }
      <PageTitle title={t('Ports')} sx={{ mt: '1rem', mb: '1.5rem' }}/>

      <Box>
      {
        deviceControl.getGroups().map((group, idx) => {
          const groups = deviceControl.getPortControls(group).filter(portCtrl => portCtrl && portCtrl.isVisible)
          if (groups.length === 0) {
            return <React.Fragment key={idx}/>
          }
          return (
            <Accordion key={idx} disableGutters className="plain" expanded={groupOpened.has(group)} onChange={(_, expanded) => onToggleGroup(group, expanded)}>
              <AccordionSummary id={`port-config-group-${group}`} className="plain" expandIcon={<ArrowForwardIos data-cy={`accordion-${group}`}/>}>{group}</AccordionSummary>
              <AccordionDetails className="plain" data-cy={`box-${group}`}>
                {
                  groups.map((portCtrl, idx) => (
                    <PortView
                      key={idx} sx={{ mt: '0.6em' }}
                      device={device}
                      portControl={portCtrl}
                      isConfigurable={true}
                      onUpdate={params => onAlterPort(portCtrl.port.port_number, params)}
                      open={portOpened.has(portCtrl.port.id as ID)}
                      onToggle={(state) => onTogglePort(portCtrl.port.id as ID, state)}
                    />
                  ))
                }
              </AccordionDetails>
            </Accordion>)
        })
      }
      </Box>
      <Box sx={{ mt: '2rem', textAlign: 'center' }}>
        <Button fullWidth onClick={onDone} data-cy="button-Save"><Trans>Save</Trans></Button>
      </Box>
    </>
  )
}
